import { useEffect, useState, useSyncExternalStore } from 'react';
import { action } from './api.ts';
import { device, useSession } from './session.tsx';
import type { Register } from './types.ts';

/**
 * Network printers as the till sees them: which register prints receipts on a printer, and
 * which printers are failing right now, so the person at the till hears it before the guest.
 */

export interface PrintProblem {
  id: string;
  name: string;
  jobs: number;
  failed: boolean;
  error: string | null;
}

let problems: PrintProblem[] = [];
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let users = 0;

export async function refreshPrintProblems() {
  try {
    const next = await action<PrintProblem[]>('printing.problems', {});
    if (JSON.stringify(next) !== JSON.stringify(problems)) {
      problems = next;
      for (const l of listeners) l();
    }
  } catch {
    // Offline or signed out: the connection bar says so already.
  }
}

/** Checks every 20 seconds while a screen shows it, and right after each print. */
export function usePrintProblems(enabled: boolean): PrintProblem[] {
  const list = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => problems,
  );
  useEffect(() => {
    if (!enabled) return;
    users++;
    if (!timer) {
      void refreshPrintProblems();
      timer = setInterval(() => void refreshPrintProblems(), 20_000);
    }
    return () => {
      users--;
      if (users === 0 && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled]);
  return enabled ? list : [];
}

/** After a print, look again a little later: by then it is out or has failed. */
export function checkSoon() {
  setTimeout(() => void refreshPrintProblems(), 2500);
}

/**
 * This device's register, when it prints receipts on a network printer. `ready` is false
 * while it is still being looked up, so an automatic print can wait for the answer.
 */
export function useReceiptPrinter(): { ready: boolean; register: Register | null } {
  const { can } = useSession();
  const allowed = can('pos.view') && can('printing.use');
  const [state, setState] = useState<{ ready: boolean; register: Register | null }>({ ready: !allowed, register: null });
  useEffect(() => {
    if (!allowed) return;
    let live = true;
    action<Register[]>('pos.registers', {}).then(
      (regs) => {
        const r = regs.find((x) => x.id === device.register) ?? (regs.length === 1 ? regs[0] : undefined);
        if (live) setState({ ready: true, register: r?.printer ? r : null });
      },
      () => live && setState({ ready: true, register: null }),
    );
    return () => {
      live = false;
    };
  }, [allowed]);
  return state;
}
